import { Button, Form, Modal } from "antd";
import React, { ReactNode, useEffect, useState } from "react";
import { ancanaApi } from "../api/ancanaApi";
import RecordFieldsMapper from "./RecordFieldsMapper";
import { CustomAction, EndpointMetadata, FieldDefinitions, Identifiable, RecordModel } from "../types/core";
import usePaginatedRecordList from "../hooks/usePaginatedRecordList";
import { ButtonType } from "antd/lib/button/button";

interface CustomActionButtonProps<RT> {
  customAction: CustomAction<RT>;
  recordModel: RecordModel<RT>;
  doRecordAction?: (actionName: string, method: 'get' | 'post' | 'patch', data?: any, detailId?: any, config?: any, onSuccess?: (res: any) => void, onError?: () => void) => void;
  query?: string;
  recordId?: number | string;
  record?: RT;
  style?: any;
  onSuccess?: any;
  type?: ButtonType;
  externalIsLoading?: boolean;
}

function CustomActionButton<RecordType extends Identifiable>(props: CustomActionButtonProps<RecordType>) {
  const { customAction, recordModel, query, record, externalIsLoading = false } = props;
  const icon= customAction.icon as ReactNode;
  const actionName= customAction.name as string;
  const queryString = !!query ? `/?${query}` : '';

  // Always call the hook, but only use it if doRecordAction is not provided
  const { doRecordAction: hookDoRecordAction, isLoading } = usePaginatedRecordList<RecordType>({
    recordPath: recordModel.path,
    apiService: 'admin',
  });

  const doRecordAction = props.doRecordAction || hookDoRecordAction;

  // Resolve confirmationProps - handle both static object and function
  const resolvedConfirmationProps = React.useMemo(() => {
    if (!customAction.confirmationProps) return undefined;

    if (typeof customAction.confirmationProps === 'function') {
      return record ? customAction.confirmationProps(record) : undefined;
    }

    return customAction.confirmationProps;
  }, [customAction.confirmationProps, record]);

  const doAction= () => {
    if (!doRecordAction) {
      console.error('doRecordAction is not available');
      return;
    }
    if (customAction.confirmationMessage || customAction.confirmationProps) {
      setConfirmationModalVisible(true);
    } else {
      doRecordAction(customAction.path+queryString, customAction.method || 'get', undefined, props.recordId, undefined, props.onSuccess);
    }
  };

  const handleConfirmAction = () => {
    if (!doRecordAction) {
      console.error('doRecordAction is not available');
      return;
    }
    setConfirmationModalVisible(false);
    doRecordAction(customAction.path+queryString, customAction.method || 'get', undefined, props.recordId, undefined, props.onSuccess);
  };

  const isPost = customAction?.method === 'post';
  const path= `${recordModel.path}/${customAction.path}`
  const [modalVisible, setModalVisible] = useState(false);
  const [confirmationModalVisible, setConfirmationModalVisible] = useState(false);
  const [pendingFormAction, setPendingFormAction] = useState<(() => void) | null>(null);
  const [fieldDefinitions, setFieldDefinitions] = useState<FieldDefinitions>();
  const [metadata, setMetadata] = useState<EndpointMetadata>();
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    ancanaApi.options(path).then(({ data: metadata }: { data: EndpointMetadata }) => {
      setMetadata(metadata);
      setFieldDefinitions(metadata.actions?.POST);
    });
  }, []);

  return (
    <>
      <Button
        icon={icon}
        onClick={(isPost && !!fieldDefinitions) ? () => setModalVisible(true) : doAction}
        style={props.style}
        type={props.type}
        loading={isLoading || externalIsLoading}
      >
        {actionName}
      </Button>

      <Modal
        visible={modalVisible}
        onCancel={() => setModalVisible(false)}
        title={metadata?.name}
        onOk={form.submit}
        okButtonProps={{ loading: saving }}
        okText={actionName}
      >
        <p className="fw-300 end-self mb-lg">{metadata?.description}</p>

        {!!fieldDefinitions &&
        <Form
          form={form}
          style={{ display: 'flex', flexDirection: 'column' }}
          onFinish={(rawData) => {
            const executeAction = () => {
              setSaving(true);
              let data: any = {};
              let config;

              // Check if any field in the metadata has type 'file upload'
              const hasFileUpload = Object.values(metadata?.actions?.POST || {}).some(
                (field: any) => field.type === 'file upload'
              );

              if (hasFileUpload) {
                // Use FormData for file uploads
                data = new FormData();
                Object.entries(rawData).forEach(([k,v]: any) => {
                  const isFileUrl = !!v && typeof v === 'string' && v.includes('https://');
                  if (!!v && !isFileUrl) data.append(k, v);
                });
                config = {
                  "headers": {
                    "content-type": 'multipart/form-data'
                  }
                }
              } else {
                // Use regular JSON data
                data = rawData;
                config = {
                  "headers": {
                    "content-type": 'application/json'
                  }
                }
              }

              if (doRecordAction) {
                doRecordAction(
                  (customAction as any).path, customAction.method || 'get', data, props.recordId, config,
                  () => {
                    setSaving(false);
                    setModalVisible(false);
                    form.resetFields();
                    props.onSuccess();
                  },
                  () => {
                    setSaving(false);
                  }
                );
              } else {
                console.error('doRecordAction is not available');
                setSaving(false);
              }
            };

            if (customAction.confirmationMessage || customAction.confirmationProps) {
              setPendingFormAction(() => executeAction);
              setConfirmationModalVisible(true);
            } else {
              executeAction();
            }
          }}
        >
          <RecordFieldsMapper<RecordType>
            fieldDefinitions={fieldDefinitions}
            recordModel={props.recordModel}
            formRef={form}
          />
        </Form>
        }
      </Modal>

      {/* Confirmation Modal */}
      <Modal
        visible={confirmationModalVisible}
        onCancel={() => {
          setConfirmationModalVisible(false);
          setPendingFormAction(null);
        }}
        title={resolvedConfirmationProps?.title || "Confirmation"}
        onOk={() => {
          if (pendingFormAction) {
            pendingFormAction();
            setPendingFormAction(null);
          } else {
            handleConfirmAction();
          }
        }}
        okText={resolvedConfirmationProps?.okText || "Continue"}
        width={resolvedConfirmationProps?.width || 520}
        style={{
          height: resolvedConfirmationProps?.height,
          // top: resolvedConfirmationProps?.height ? '5vh' : undefined,
          ...resolvedConfirmationProps?.style
        }}
        bodyStyle={{
          height: resolvedConfirmationProps?.height
            ? `calc(${resolvedConfirmationProps.height} - 110px)`
            : 'auto',
          padding: !!resolvedConfirmationProps ? 0 : '24px'
        }}
      >
        {resolvedConfirmationProps?.body || customAction.confirmationMessage}
      </Modal>
    </>
  )
}

export default CustomActionButton;
